/**
 * Data stays fresh on its own (owner 2026-11-04: «хеч кандай рефреш бериши
 * кк эмас, бекграунда ишлаши кк»). The open lists, the dropdown references
 * and the screens' own pickers are fetched again in the background:
 *
 * - after a successful write in this tab (a create, an edit, a delete);
 * - after a write in another open tab of the admin (BroadcastChannel);
 * - when the tab shows again or the window gets focus (at most once per gap,
 *   unless something changed meanwhile);
 * - when the connection comes back.
 *
 * A refresh reloads the ListStore lists on screen, the lookups in use and
 * every effect that follows the refresh tick (stores/refreshSignal).
 * A hidden tab only notes a change and refreshes when it shows. Nothing polls.
 * Typed form values never reset: an open record is not reloaded, only lists.
 * Kept free of app imports so `node --test scripts/freshness.test.ts` runs it.
 */

// Writes that change no list: sign-in, presence, chat, own settings, uploads, previews and exports.
const ignoredWrites = /(^|\/)(auth|presence|chats|notifications|me|file-upload)(\/|$)|\/(preview|export)$/

/** A write that may change what a list or a dropdown shows. */
export function isDataMutation(method?: string, url?: string) {
  if (!method || !['post', 'put', 'patch', 'delete'].includes(method.toLowerCase())) return false
  const path = (url ?? '').replace(/^[a-z]+:\/\/[^/]+/i, '').replace(/[?#].*$/, '').replace(/^\/?(v1\/)?/, '')
  return !ignoredWrites.test(path)
}

type Env = {
  now: () => number
  visible: () => boolean
  setTimeout: (fn: () => void, ms: number) => number
  clearTimeout: (id: number) => void
}

/** The rules alone: when to run `refresh`. The browser wiring is in `startFreshness`. */
export function createFreshness(refresh: () => void, { env, debounceMs = 400, focusGapMs = 30_000 }: { env: Env; debounceMs?: number; focusGapMs?: number }) {
  let stale = false
  let last = env.now()
  let timer: number | null = null

  const schedule = () => {
    if (timer !== null) env.clearTimeout(timer)
    timer = env.setTimeout(() => {
      timer = null
      if (!env.visible()) return
      stale = false
      last = env.now()
      refresh()
    }, debounceMs)
  }

  return {
    /** Something changed (here or in another tab). */
    mutated() {
      stale = true
      if (env.visible()) schedule()
    },
    /** The tab shows again or the window got focus. */
    focused() {
      if (env.visible() && (stale || env.now() - last >= focusGapMs)) schedule()
    },
    online() {
      stale = true
      if (env.visible()) schedule()
    },
    /** «Обновить» in the top bar just fetched everything. */
    refreshed() {
      stale = false
      last = env.now()
    },
    stop() {
      if (timer !== null) env.clearTimeout(timer)
      timer = null
    },
  }
}

/* Browser wiring. */

const channelName = 'myfidani-admin-data'
let active: (ReturnType<typeof createFreshness> & { channel: BroadcastChannel | null }) | null = null

/** A write succeeded here: refresh this tab and tell the other tabs. Called by the API client. */
export function notifyMutation() {
  if (!active) return
  active.mutated()
  active.channel?.postMessage({ type: 'mutated' })
}

export function markRefreshed() {
  active?.refreshed()
}

/** Starts the background refresh for the signed-in shell; returns the stop function. */
export function startFreshness(refresh: () => void) {
  active?.stop()
  const rules = createFreshness(refresh, {
    env: {
      now: () => Date.now(),
      visible: () => document.visibilityState === 'visible',
      setTimeout: (fn, ms) => window.setTimeout(fn, ms),
      clearTimeout: (id) => window.clearTimeout(id),
    },
  })
  const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(channelName)
  if (channel) channel.onmessage = (event: MessageEvent<{ type?: string }>) => event.data?.type === 'mutated' && rules.mutated()
  const onVisible = () => rules.focused()
  const onOnline = () => rules.online()
  window.addEventListener('focus', onVisible)
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('online', onOnline)
  const current = { ...rules, channel }
  active = current

  return () => {
    rules.stop()
    channel?.close()
    window.removeEventListener('focus', onVisible)
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('online', onOnline)
    if (active === current) active = null
  }
}
